import React, { useState, useEffect } from "react";

// Librerias
import { Dropdown } from "semantic-ui-react";
import Joi from "joi";
import PropTypes from "prop-types";

// Lodash
import { filter, map, isEqual, trim, get, isEmpty, isArray, sortBy } from "lodash";
import { connect } from "react-redux";
import genericAction from "@Actions/generic";

// Components
import Input from "@Components/Input";
import IKModal from "@Components/IkModal";
import FieldsEditor from "../../Components/FieldsEditor";

// Modals
import ConfirmModal from "@Modals/ConfirmModal";

// Static Data
import { ACTION_TYPE_OPTIONS, HTTP_METHOD_OPTIONS } from "./staticData";

// Styles
import styles from "./styles.module.css";

const ActionsChatBotCrud = ({ modalCrud, tableValue, onClose, dispatch, refresh }) => {
	const [open, setOpen] = useState(false);
	const [modalOption, setModalOption] = useState("");
	const [companyId, setCompanyId] = useState(null);
	const [name, setName] = useState("");
	const [func, setFunc] = useState("");
	const [fields, setFields] = useState([]);
	const [initialFormData, setInitialFormData] = useState({});
	const [companyOptions, setCompanyOptions] = useState([]);
	const [errors, setErrors] = useState({
		type: "",
		functionErr: "",
		httpMethod: "",
		endpoint: "",
	});
	const [fieldErrors, setFieldErrors] = useState({});
	const [type, setType] = useState(get(tableValue, "type", "internal"));
	const [httpMethod, setHttpMethod] = useState(get(tableValue, "method", null));
	const [endpoint, setEndpoint] = useState(get(tableValue, "endpoint", ""));

	const fieldTypeEnum = ["string", "number", "date", "file", "dropdown", "group", "menu"];
	const acquisitionEnum = ["static", "input", "generated"];

	const parseRawOptions = (inputValue) => {
		if (!inputValue || inputValue.trim() === "") return [];

		// Si contiene saltos de línea, usar split por líneas
		if (inputValue.includes("\n")) {
			return inputValue
				.split("\n")
				.map((option) => {
					return option.trim();
				})
				.filter((option) => {
					return option !== "";
				});
		}

		// Si contiene comas, usar split por comas
		if (inputValue.includes(",")) {
			return inputValue
				.split(",")
				.map((option) => {
					return option.trim();
				})
				.filter((option) => {
					return option !== "";
				});
		}

		// Si es una sola línea, tratarla como una opción
		return [inputValue.trim()].filter((option) => {
			return option !== "";
		});
	};

	const actionFieldSchema = Joi.object({
		type: Joi.string()
			.valid(...fieldTypeEnum)
			.required()
			.messages({
				"any.only": "Tipo inválido",
				"string.empty": "Tipo es requerido",
				"any.required": "Tipo es requerido",
			}),
		name: Joi.alternatives().conditional("acquisition", {
			is: "static",
			then: Joi.string().trim().min(1).required().messages({
				"string.empty": "Nombre del campo es requerido",
				"any.required": "Nombre del campo es requerido",
			}),
			otherwise: Joi.string().allow("", null).optional(),
		}),

		label: Joi.string().trim().min(1).required().messages({
			"string.empty": "Label del campo es requerido",
			"any.required": "Label del campo es requerido",
		}),
		order: Joi.number().required().messages({
			"any.required": "Orden es requerido",
			"number.base": "Orden debe ser numérico",
		}),
		acquisition: Joi.string()
			.valid(...acquisitionEnum)
			.required()
			.messages({
				"any.only": "Acquisition inválida",
				"string.empty": "Acquisition es requerida",
				"any.required": "Acquisition es requerida",
			}),
		question: Joi.alternatives().conditional("acquisition", {
			is: "input",
			then: Joi.string().trim().min(1).required().messages({
				"any.required": "Pregunta es requerida",
				"string.empty": "Pregunta es requerida",
			}),
			otherwise: Joi.string().allow("", null).optional(),
		}),
		value: Joi.any().allow(null).optional(),
		inputOptions: Joi.alternatives().conditional("type", {
			is: "dropdown",
			then: Joi.alternatives()
				.try(
					Joi.array().items(Joi.string().trim().min(1)).min(1).messages({
						"array.min": "Debe agregar al menos una opción",
						"any.required": "Las opciones son requeridas para dropdown",
					}),
					Joi.string().trim().min(1).messages({
						"string.empty": "Debe agregar al menos una opción",
						"string.min": "Debe agregar al menos una opción",
					})
				)
				.required()
				.messages({
					"any.required": "Las opciones son requeridas para dropdown",
				}),
			otherwise: Joi.array().items(Joi.string()).default([]).optional(),
		}),
		inputOptionsRaw: Joi.alternatives().conditional("type", {
			is: "dropdown",
			then: Joi.string().trim().min(1).required().messages({
				"string.empty": "Debe agregar al menos una opción",
				"string.min": "Debe agregar al menos una opción",
				"any.required": "Las opciones son requeridas para dropdown",
			}),
			otherwise: Joi.string().optional(),
		}),
		options: Joi.array().items(Joi.string()).optional(),
		id: Joi.any().optional(),
		active: Joi.boolean().optional(),
	});

	const fieldsArraySchema = Joi.array().items(actionFieldSchema).min(1).required().messages({
		"array.base": "Fields debe ser una lista",
		"array.min": "Debes agregar al menos un Campo",
		"any.required": "Fields es requerido",
	});

	const schema = Joi.object({
		name: Joi.string().trim().min(2).required().messages({
			"string.empty": "Nombre es un campo requerido",
			"string.min": "Nombre debe tener mínimo 2 caracteres",
		}),
		functionErr: Joi.string().required().messages({
			"any.required": "Función es un campo requerido",
			"string.empty": "Función es un campo requerido",
		}),

		type: Joi.string().empty(["", null]).valid("internal", "external").required().messages({
			"any.required": "Tipo es requerido",
			"string.empty": "Tipo es requerido",
			"string.base": "Tipo es requerido",
			"any.only": "Tipo inválido",
		}),

		httpMethod: Joi.alternatives().conditional("type", {
			is: "external",
			then: Joi.string().valid("GET", "POST", "PUT", "PATCH", "DELETE").required().messages({
				"any.only": "Método inválido",
				"any.required": "Método es requerido",
				"string.base": "Método es requerido",
				"string.empty": "Método es requerido",
			}),
			otherwise: Joi.valid(null, "").strip(),
		}),

		endpoint: Joi.string()
			.trim()
			.when("type", { is: "external", then: Joi.string().min(1).required(), otherwise: Joi.allow("", null) })
			.messages({
				"string.empty": "Endpoint es requerido",
				"any.required": "Endpoint es requerido",
			}),
		fields: fieldsArraySchema,
	});

	useEffect(() => {
		setModalOption(modalCrud);
		(async () => {
			const companiesResponse = await dispatch(genericAction({ method: "get", route: "companies/all" }));
			const options = map(filter(companiesResponse, { isActive: true }), (c) => {
				return { key: c.id, value: c.id, text: c.name };
			});
			setCompanyOptions(options);

			const formData = {
				companyId: get(tableValue, "companyId", null),
				name: get(tableValue, "name", "") || "",
				function: get(tableValue, "function", "") || "",
				type: get(tableValue, "type", "internal"),
				method: get(tableValue, "method", null),
				endpoint: get(tableValue, "endpoint", "") || "",
				fields: isArray(tableValue?.fields) ? tableValue.fields : [],
			};

			setCompanyId(formData.companyId);
			setName(formData.name);
			setFunc(formData.function);
			setType(formData.type);
			setHttpMethod(formData.method);
			setEndpoint(formData.endpoint);
			setFields(formData.fields);
			setInitialFormData(formData);
		})();
	}, [tableValue, modalCrud, dispatch]);

	const validateEachField = (list) => {
		const errs = {};
		list.forEach((f, idx) => {
			const { error } = actionFieldSchema.validate(f, { abortEarly: false });
			if (error) {
				const id = f?.id ?? idx;
				errs[id] = {};
				error.details.forEach((d) => {
					const key = d.path?.[0];
					if (key) errs[id][key] = d.message;
				});
			}
		});
		const orders = list.map((x) => {
			return x.order;
		});
		const dups = orders.filter((o, i) => {
			return orders.indexOf(o) !== i;
		});
		if (dups.length) {
			list.forEach((f, idx) => {
				if (dups.includes(f.order)) {
					const id = f?.id ?? idx;
					errs[id] = { ...(errs[id] || {}), order: "Orden duplicado" };
				}
			});
		}
		return errs;
	};

	const onSuccess = () => {
		if (modalOption === "Add") {
			setCompanyId(null);
			setName("");
			setFunc("");
			setFields([]);
			setInitialFormData({ companyId: null, name: "", function: "" });
		} else {
			onClose();
		}
		if (refresh) {
			refresh();
		}
	};

	// Filtra por label, ordena por order y devuelve fields listos con sus props.
	const prepareFields = (list, { mode } = {}) => {
		return map(
			sortBy(
				filter(list, (f) => {
					return trim(f?.label || "");
				}),
				(f) => {
					return Number(f.order) || 0;
				}
			),
			(f, i) => {
				const base = {
					type: f.type,
					name: trim(f.name || ""),
					acquisition: f.acquisition,
					label: f.label,
					order: i + 1,
					value: null,
					question: f.acquisition === "input" ? trim(f.question || "") : null,
					inputOptions: f.type === "dropdown" ? f.inputOptions || parseRawOptions(f.inputOptionsRaw) || f.options || [] : [],
				};
				const acqPart = {};
				const optionsPart = {};
				const valuePart = {};
				const idPart = mode === "Add" ? {} : f.id && !String(f.id).startsWith("fld_") ? { id: f.id } : {};

				return { ...base, ...acqPart, ...optionsPart, ...valuePart, ...idPart };
			}
		);
	};

	const handlerSaved = async () => {
		const validationObj = { name, functionErr: func, type, ...(type === "external" ? { httpMethod, endpoint } : {}), fields };
		const { error } = schema.validate(validationObj, { abortEarly: false });

		const perFieldErrors = validateEachField(fields);
		const hasFieldErrors = Object.keys(perFieldErrors).length > 0;

		if (error || hasFieldErrors) {
			const errorsVal = {
				name: "",
				functionErr: "",
				fields: "",
				type: "",
				httpMethod: "",
				endpoint: "",
			};

			// Mensajes de name y method
			error?.details?.forEach((item) => {
				const root = item.path?.[0];
				if (root === "name" || root === "functionErr") errorsVal[root] = item.message;
				if (root === "type") errorsVal.type = item.message;
				if (root === "httpMethod") errorsVal.httpMethod = item.message;
				if (root === "endpoint") errorsVal.endpoint = item.message;
			});

			const fieldsMin = error?.details?.find((d) => {
				return d.path?.[0] === "fields" && d.type === "array.min";
			});
			errorsVal.fields = fieldsMin ? fieldsMin.message : "";

			setErrors(errorsVal);
			setFieldErrors(perFieldErrors);
			return;
		}

		setErrors({ name: "", functionErr: "", fields: "", type: "" });
		setFieldErrors({});

		const preparedFields = prepareFields(fields, { mode: modalOption });
		const sanitizeFunc = (s) => {
			return String(s ?? "").replace(/\s+/g, "");
		};

		if (modalOption === "Add") {
			const dataEditor = {
				companyId: companyId == null ? null : companyId,
				name: trim(name),
				type,
				function: sanitizeFunc(func),
				fields: preparedFields,
				...(type === "external" ? { method: httpMethod, endpoint: trim(endpoint) } : {}),
			};

			await dispatch(genericAction({ method: "post", route: "actions", body: dataEditor, onSuccess }));
		} else {
			const dataEditor = {};
			const initialPreparedFields = prepareFields(initialFormData.fields || [], { mode: "Edit" });
			const fn = sanitizeFunc(func);

			if (companyId !== initialFormData.companyId) {
				dataEditor.companyId = companyId && companyId !== "" ? companyId : null;
			}

			if (name !== initialFormData.name) dataEditor.name = trim(name);
			if (type !== initialFormData.type) dataEditor.type = type;
			if (func !== initialFormData.function) dataEditor.function = fn;
			if (!isEqual(preparedFields, initialPreparedFields)) {
				dataEditor.fields = preparedFields;
			}

			if (type === "external") {
				if (httpMethod !== initialFormData.method) dataEditor.method = httpMethod;
				if (endpoint !== initialFormData.endpoint) dataEditor.endpoint = trim(endpoint);
			} else if (initialFormData.type === "external") {
				dataEditor.method = null;
				dataEditor.endpoint = null;
			}

			await dispatch(genericAction({ method: "patch", route: `actions/${tableValue.id}`, body: dataEditor, onSuccess }));
		}
	};

	const cancelModal = () => {
		return onClose();
	};

	const stripUi = (arr = []) => {
		return map(arr, ({ active, ...rest }) => {
			return rest;
		});
	};

	const modalOpen = () => {
		const unchanged =
			isEqual(companyId ?? null, initialFormData.companyId ?? null) &&
			isEqual(name || "", initialFormData.name || "") &&
			isEqual(func || "", initialFormData.function || "") &&
			isEqual(type || "internal", initialFormData.type || "internal") &&
			isEqual(httpMethod ?? null, initialFormData.method ?? null) &&
			isEqual(endpoint || "", initialFormData.endpoint || "") &&
			isEqual(stripUi(fields), stripUi(initialFormData.fields));

		if (unchanged) {
			cancelModal();
		} else if (open) {
			setOpen(false);
		} else {
			setOpen(true);
		}
	};

	return (
		<>
			<IKModal
				title={modalOption === "Add" ? "Agregar Acción" : `Acción: ${String(initialFormData?.name || "")}`}
				id={tableValue.id}
				mode={modalOption}
				setMode={setModalOption}
				onClose={modalOpen}
				onSuccess={handlerSaved}
				className={styles.modalFirst}
				fullHeight>
				<div className={styles.whiteContainer}>
					<div className={styles.containerCompany}>
						<div className={styles.fieldCompany}>
							<label className={styles.label}>Compañía:</label>
							<Dropdown
								placeholder="Selecciona compañía"
								fluid
								clearable
								search
								selection
								disabled={modalOption === "Watch"}
								options={companyOptions}
								value={companyId}
								onChange={(_, { value }) => {
									return setCompanyId(value);
								}}
								className={styles.inputClassCompany}
							/>
						</div>
						<div className={styles.field}>
							<label className={styles.label}>
								Nombre:<span className={styles.required}> *</span>
							</label>
							<Input
								name="name"
								placeholder="Ej. Enviar saludos"
								value={name}
								onChange={(_, { value }) => {
									return setName(value);
								}}
								disabled={modalOption === "Watch"}
								error={
									!isEmpty(errors.name) && {
										content: errors.name,
										pointing: "above",
										className: styles.messageError,
									}
								}
							/>
						</div>
					</div>
					<div className={styles.containerNM}>
						<div className={styles.fieldType}>
							<label className={styles.label}>
								Tipo: <span className={styles.required}>*</span>
							</label>
							<Dropdown
								placeholder="Selecciona un tipo"
								fluid
								selection
								disabled={modalOption === "Watch"}
								options={ACTION_TYPE_OPTIONS}
								value={type}
								onChange={(_, { value }) => {
									return setType(value);
								}}
								className={styles.inputClassType}
							/>
							{!isEmpty(errors.type) && <div className="ui pointing red basic label">{errors.type}</div>}
						</div>
						<div className={styles.field}>
							<label className={styles.label}>
								Función: <span className={styles.required}>*</span>
							</label>
							<Input
								name="func"
								placeholder="Ej: sendGreeting"
								value={func}
								onChange={(_, { value }) => {
									return setFunc(value);
								}}
								disabled={modalOption === "Watch"}
								error={
									!isEmpty(errors.functionErr) && {
										content: errors.functionErr,
										pointing: "above",
										className: styles.messageError,
									}
								}
							/>
						</div>
					</div>
					{type === "external" && (
						<div className={styles.containerTypeInto}>
							<div className={styles.fieldMethod}>
								<label className={styles.label}>
									Método: <span className={styles.required}>*</span>
								</label>
								<Dropdown
									placeholder="Click para Seleccionar"
									fluid
									selection
									disabled={modalOption === "Watch"}
									options={HTTP_METHOD_OPTIONS}
									value={httpMethod}
									onChange={(_, { value }) => {
										return setHttpMethod(value);
									}}
									className={styles.inputClassTypeMethod}
								/>
								{!isEmpty(errors.httpMethod) && <div className="ui pointing red basic label">{errors.httpMethod}</div>}
							</div>

							<div className={styles.fieldEndPoint}>
								<label className={styles.label}>
									Endpoint: <span className={styles.required}>*</span>
								</label>
								<Input
									name="endpoint"
									placeholder="Ej.: https://api.midominio.com/recurso"
									value={endpoint}
									onChange={(_, { value }) => {
										return setEndpoint(value);
									}}
									disabled={modalOption === "Watch"}
									className={styles.inputClassTypeEndPoint}
									error={
										!isEmpty(errors.endpoint) && {
											content: errors.endpoint,
											pointing: "above",
											className: styles.messageError,
										}
									}
								/>
							</div>
						</div>
					)}

					<FieldsEditor
						value={fields}
						onChange={setFields}
						errors={fieldErrors}
						readOnly={modalOption === "Watch"}
						modalOption={modalOption}
					/>

					{!isEmpty(errors.fields) && (
						<div className="ui pointing red basic label" role="alert" style={{ marginTop: -15 }}>
							{errors.fields}
						</div>
					)}
				</div>
			</IKModal>

			{open && (
				<ConfirmModal
					buttonLabel="Salir"
					visible
					header="Confirmar Cerrar"
					content="Está seguro que desea salir del formulario"
					onConfirm={cancelModal}
					onDismiss={modalOpen}
				/>
			)}
		</>
	);
};

function mapStateToProps({ user, company }) {
	return {
		userName: user?.fullName ?? user?.name,
		companyName: company?.name,
		currentCompanyId: company?.companyId,
		companies: company?.companies || [],
	};
}

ActionsChatBotCrud.propTypes = {
	tableValue: PropTypes.array,
	onClose: PropTypes.func,
	dispatch: PropTypes.func,
	modalCrud: PropTypes.string,
	refresh: PropTypes.func,
};

export default connect(mapStateToProps)(ActionsChatBotCrud);
